import { CoinTopUpMessage } from '@repo/studio-components/coin-top-up-message'
import { LeftSheet } from '@repo/studio-design-system-ui-sheets/left-sheet'
import { Button } from '@repo/studio-design-system-ui-button'
import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import { ButtonTooltip } from '@repo/studio-design-system-ui-tooltip'
import { SkipTrialMessage } from '@repo/studio-components/skip-trial-message'
import { m, MotionConfig } from 'framer-motion'
import * as React from 'react'
import * as Popover from 'primitives/popover'
import { tw } from 'utils/classnames'
import { FreeAccessMessage } from '../free-access-message'
import { GoUnlimitedMessage } from '../go-unlimited-message'
import { NsfwPlatformHidden } from '@repo/studio-config/nsfw-platform-hidden'
import { useStudioConfig, useStudioTranslate } from '@repo/studio-config/studio-config-context'
import type {
  CategoryNavigationSection,
  CompactNavigationMenuConfig,
  ContextualNavigationConfig,
  HomeNavigationSection,
} from '@repo/studio-config/studio-config-context'
import { NavigationCluster, NavigationClusterLink } from './navigation-cluster'
import {
  leftoverCategoryClusters,
  useAfterHomeClusters,
  useNavigationFooter,
  useNavigationPromo,
  useNavigationSections,
  usePinnedToolsByCluster,
  useToolSections,
  useToolsByRoute,
} from './navigation-cluster-data'
import type { Cluster } from './navigation-cluster-data'
import { NavigationItem } from './navigation-item'
import {
  SingularNavigationItems,
  NavigationHomeItem,
  NavigationSectionTitle,
} from './navigation-items'
import { StackedNavigationItems } from './stacked-navigation-items'
import { NavigationFooter, StackedNavigationFooter } from './navigation-footer'
import { PinnedToolsMenu } from './pinned-tools-menu'
import { useHasStudioUnlimitedQuery } from '@repo/cf-user/hooks/use-user-data'
import { useIsFreemode } from '@repo/studio-config/app-source'
import { useIsStudioAIUserQuery } from '@repo/studio-hooks-studio-ai/use-is-studio-ai-user'
import { useIsTrialUserQuery } from '@repo/studio-hooks-auth/use-is-trial-user-query'
import { EarnIconButton } from '@repo/studio-components/earn-button'
import {
  EarnMoreCoinsButton,
  EarnMoreCoinsIconButton,
} from '@repo/studio-components/earn-more-coins-button'
import { NavigationUsersOnline } from './navigation-users-online'
import { ContextualNavigationPanel, useActiveContextualNavigation } from './contextual-navigation'

interface NavigationSideBarProps extends React.ComponentProps<typeof m.div> {
  open: boolean
}

const sideBarVariants = {
  open: {
    width: '245px',
  },
  closed: {
    width: '60px',
  },
}

// The extra pixel keeps bordered promo cards from anti-aliasing at the clipped panel seam.
const hiddenPrimaryPanelClass = '-translate-x-[calc(100%+1px)]'

export function NavigationSideBar({ open, ...props }: NavigationSideBarProps) {
  const contextualNavigation = useActiveContextualNavigation()

  return (
    <NavigationSideBarContent
      key={contextualNavigation?.id ?? 'primary'}
      open={open}
      contextualNavigation={contextualNavigation}
      {...props}
    />
  )
}

function NavigationSideBarContent({
  open,
  contextualNavigation,
  ...props
}: NavigationSideBarProps & {
  contextualNavigation?: ContextualNavigationConfig
}) {
  const { data: isStudioAiUser } = useIsStudioAIUserQuery()
  const { data: hasStudioUnlimited } = useHasStudioUnlimitedQuery()
  const toolSections = useToolSections()
  const afterHomeClusters = useAfterHomeClusters()
  const pinnedTools = usePinnedToolsByCluster()
  const navigationSections = useNavigationSections()
  const footerItems = useNavigationFooter()
  const promo = useNavigationPromo()
  const showUnlimitedPromo = promo === 'unlimited' && hasStudioUnlimited === false
  const showCoinsPromo = promo === 'coins'
  const [showContextualNavigation, setShowContextualNavigation] = React.useState(
    Boolean(contextualNavigation),
  )

  return (
    <m.div
      initial={open ? 'open' : 'closed'}
      animate={open ? 'open' : 'closed'}
      variants={sideBarVariants}
      {...props}
      className={tw('flex h-full shrink-0 flex-col bg-gray-900', props.className)}
    >
      <div className="relative min-h-0 grow overflow-hidden">
        <div
          aria-hidden={showContextualNavigation}
          inert={showContextualNavigation ? true : undefined}
          className={tw(
            'absolute inset-0 flex flex-col transition-transform duration-200 ease-out motion-reduce:transition-none',
            showContextualNavigation ? hiddenPrimaryPanelClass : 'translate-x-0',
          )}
        >
          <div
            className="flex grow flex-col overflow-x-clip border-r border-gray-700-opacity pt-[14px] scroller-xs-stable"
            style={{ '--scroller-gutter': '14px' } as React.CSSProperties}
          >
            <div className="flex flex-col w-full">
              {navigationSections.map((section, index) => {
                if (section.kind === 'home') {
                  return (
                    <HomeSection
                      key="home"
                      section={section}
                      afterHomeClusters={afterHomeClusters}
                      expanded={open}
                    />
                  )
                }
                if (section.kind === 'pinned') {
                  return <PinnedSection key="pinned" pinnedTools={pinnedTools} expanded={open} />
                }
                if (section.kind === 'category') {
                  const toolSection = toolSections[index] ?? {
                    clusters: [],
                    unclusteredTools: [],
                  }
                  const hasSingularEntries = section.entries.some(
                    entry => entry.kind === 'singular',
                  )
                  if (
                    !hasSingularEntries &&
                    toolSection.clusters.length === 0 &&
                    toolSection.unclusteredTools.length === 0
                  ) {
                    return null
                  }
                  return (
                    <NavigationSection key={`category-${index}`}>
                      <CategorySectionContent
                        section={section}
                        toolSection={toolSection}
                        expanded={open}
                      />
                    </NavigationSection>
                  )
                }
                return null
              })}
            </div>
            {showUnlimitedPromo || showCoinsPromo ? (
              <div className="flex flex-col flex-grow items-start justify-end pt-4 mb-4">
                {showUnlimitedPromo ? <GoUnlimitedMessage className="w-full" show={open} /> : null}
                {showCoinsPromo && !isStudioAiUser ? (
                  <FreeAccessMessage className="w-full" show={open} />
                ) : null}
                {showCoinsPromo && isStudioAiUser ? <CoinMessages show={open} /> : null}
              </div>
            ) : null}
          </div>
        </div>

        {contextualNavigation ? (
          <div
            aria-hidden={!showContextualNavigation}
            inert={!showContextualNavigation ? true : undefined}
            className={tw(
              'absolute inset-0 flex flex-col overflow-x-clip border-r border-gray-700-opacity pt-[14px] scroller-xs-stable transition-transform duration-200 ease-out motion-reduce:transition-none',
              showContextualNavigation ? 'translate-x-0' : 'translate-x-full',
            )}
            style={{ '--scroller-gutter': '14px' } as React.CSSProperties}
          >
            <ContextualNavigationPanel
              expanded={open}
              navigation={contextualNavigation}
              onBack={() => setShowContextualNavigation(false)}
            />
          </div>
        ) : null}
      </div>

      <div
        className={tw(
          'flex shrink-0 gap-1 border-r border-t border-r-gray-700-opacity border-t-gray-800 bg-gray-900 px-[14px] py-[14px]',
          open ? 'flex-row items-center justify-end' : 'flex-col items-start',
        )}
      >
        <MotionConfig transition={{ duration: 0.2, ease: 'easeInOut' }}>
          <NavigationFooter items={footerItems} expanded={open} />
        </MotionConfig>
      </div>
    </m.div>
  )
}

function NavigationSection({ children }: { children: React.ReactNode }) {
  return <div className="py-3 border-b border-b-gray-800 flex flex-col gap-1">{children}</div>
}

function HomeSection({
  section,
  afterHomeClusters,
  expanded,
}: {
  section: HomeNavigationSection
  afterHomeClusters: Cluster[]
  expanded: boolean
}) {
  return (
    <>
      <div className="pb-3 border-b border-b-gray-800 flex flex-col gap-1">
        <NavigationHomeItem route={section.route} expanded={expanded} />
        <SingularNavigationItems items={section.items} expanded={expanded} />
      </div>
      {afterHomeClusters.length > 0 ? (
        <NavigationSection>
          {afterHomeClusters.map(cluster => (
            <ClusterRow key={cluster.cluster} cluster={cluster} expanded={expanded} />
          ))}
        </NavigationSection>
      ) : null}
    </>
  )
}

function PinnedSection({
  pinnedTools,
  expanded,
}: {
  pinnedTools: ReturnType<typeof usePinnedToolsByCluster>
  expanded: boolean
}) {
  return (
    <NsfwPlatformHidden>
      {pinnedTools ? (
        <div className="py-3 border-b border-b-gray-800">
          <PinnedToolsMenu expanded={expanded} pinnedTools={pinnedTools} />
        </div>
      ) : null}
    </NsfwPlatformHidden>
  )
}

function ClusterRow({ cluster, expanded }: { cluster: Cluster; expanded: boolean }) {
  const isFreemode = useIsFreemode()

  if (cluster.behaviour === 'link') {
    return (
      <NavigationClusterLink
        route={cluster.route}
        label={cluster.label}
        icon={cluster.icon}
        showLabel={expanded}
      />
    )
  }

  const route = isFreemode && cluster.cluster === 'image' ? '/flow' : cluster.route

  return (
    <Popover.Root
      behaviour={route ? 'both' : 'hover'}
      focusBehaviour="none"
      blockPointerEvents
      safePolygonBulge={18}
      safePolygonRequireIntent={false}
      safePolygonRestMs={500}
    >
      <NavigationCluster
        cluster={cluster.cluster}
        items={cluster.items.toSorted((a, b) => a.order - b.order)}
        navigationOnlyItems={cluster.navigationOnlyItems}
        expanded={expanded}
        route={route}
      />
    </Popover.Root>
  )
}

const sectionTitleMotion = {
  open: { width: 216, opacity: 1, height: 26 },
  closed: { width: 0, opacity: 0, height: 0, padding: 0 },
}

function CategorySectionHeading({
  section,
  expanded,
}: {
  section: CategoryNavigationSection
  expanded: boolean
}) {
  const motionProps = {
    initial: expanded ? 'open' : 'closed',
    animate: expanded ? 'open' : 'closed',
    variants: sectionTitleMotion,
  } as const

  if (section.title) {
    return (
      <m.span
        className="px-2 pb-2 typography-12 text-gray-500 line-clamp-1 overflow-clip h-[26px]"
        {...motionProps}
      >
        {section.title}
      </m.span>
    )
  }

  if (section.category) {
    return <NavigationSectionTitle category={section.category} {...motionProps} />
  }

  return null
}

function CategorySectionContent({
  section,
  toolSection,
  expanded,
}: {
  section: CategoryNavigationSection
  toolSection: ReturnType<typeof useToolSections>[number]
  expanded: boolean
}) {
  const leftoverClusters = leftoverCategoryClusters(section, toolSection.clusters)
  const toolsByRoute = useToolsByRoute()

  return (
    <>
      <CategorySectionHeading section={section} expanded={expanded} />
      {section.entries.map(entry => {
        if (entry.kind === 'singular') {
          return (
            <NavigationItem
              key={entry.route}
              title={entry.title}
              route={entry.route}
              expanded={expanded}
              icon={entry.icon}
              exact={entry.exact}
              hardNavigation={entry.hardNavigation}
              tool={toolsByRoute.get(entry.route)}
            />
          )
        }
        const cluster = toolSection.clusters.find(item => item.cluster === entry.cluster)
        return cluster ? (
          <ClusterRow key={cluster.cluster} cluster={cluster} expanded={expanded} />
        ) : null
      })}
      {leftoverClusters.map(cluster => (
        <ClusterRow key={cluster.cluster} cluster={cluster} expanded={expanded} />
      ))}
      {toolSection.unclusteredTools.map(item => (
        <NavigationItem
          key={item.title}
          title={item.title}
          route={item.route}
          expanded={expanded}
          icon={item.icon}
        />
      ))}
    </>
  )
}

interface NavigationSideSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  activeMenuId: string | null
  onActiveMenuIdChange: (menuId: string | null) => void
  className?: string
  headerRef?: React.RefObject<HTMLElement | null>
}

export function NavigationSideSheet({
  open,
  className,
  onOpenChange,
  activeMenuId,
  onActiveMenuIdChange,
  headerRef,
}: NavigationSideSheetProps) {
  const contextualNavigation = useActiveContextualNavigation()
  const compactNavigationMenus = useStudioConfig()?.compactNavigationMenus ?? []
  const resetKey = [
    contextualNavigation?.id ?? 'primary',
    ...compactNavigationMenus.map(m => m.id),
  ].join(':')

  return (
    <NavigationSideSheetContent
      key={resetKey}
      open={open}
      className={className}
      onOpenChange={onOpenChange}
      activeMenuId={activeMenuId}
      onActiveMenuIdChange={onActiveMenuIdChange}
      headerRef={headerRef}
      contextualNavigation={contextualNavigation}
      compactNavigationMenus={compactNavigationMenus}
    />
  )
}

function NavigationSideSheetContent({
  open,
  className,
  onOpenChange,
  activeMenuId,
  onActiveMenuIdChange,
  headerRef,
  contextualNavigation,
  compactNavigationMenus,
}: NavigationSideSheetProps & {
  contextualNavigation?: ContextualNavigationConfig
  compactNavigationMenus: CompactNavigationMenuConfig[]
}) {
  const [topOffset, setTopOffset] = React.useState<number | null>(null)
  const panelId = React.useId()
  const activeCompactMenu = compactNavigationMenus.find(menu => menu.id === activeMenuId)
  const ActiveCompactMenuContent = activeCompactMenu?.Content
  const activeContextualNavigation =
    contextualNavigation?.id === activeMenuId ? contextualNavigation : undefined
  const hasActiveCompactMenu = Boolean(activeCompactMenu)
  const hasActiveContextualNavigation = Boolean(activeContextualNavigation)
  const hasActiveMenu = hasActiveCompactMenu || hasActiveContextualNavigation

  const { data: isStudioAiUser } = useIsStudioAIUserQuery()
  const { data: hasStudioUnlimited } = useHasStudioUnlimitedQuery()
  const footerItems = useNavigationFooter()
  const promo = useNavigationPromo()
  const showUnlimitedPromo = promo === 'unlimited' && hasStudioUnlimited === false
  const showCoinsPromo = promo === 'coins'
  const t = useStudioTranslate()

  React.useLayoutEffect(() => {
    const element = headerRef?.current
    if (!element) {
      setTopOffset(null)
      return
    }
    const rect = element.getBoundingClientRect()
    setTopOffset(rect.top + rect.height)
  }, [headerRef, open])

  const sidesheetTop = topOffset ?? 0

  const resetActiveMenu = () => {
    onActiveMenuIdChange(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetActiveMenu()
    }
    onOpenChange(nextOpen)
  }

  const handleNavigate = () => {
    resetActiveMenu()
    onOpenChange(false)
  }

  /* Hey developer! If you are working here, please try and remove this z-50 if possible. If it is not possible to remove it, then remove this comment. It's bad practice to use z-indexes, so please avoid where possible. Portals should be enough. */
  return (
    <LeftSheet
      open={open}
      onOpenChange={handleOpenChange}
      className={tw(
        "@container/navigation-sheet z-50 bg-transparent border-r border-gray-700-opacity !w-[245px] !max-w-[245px] !rounded-none before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[245px] before:bg-gray-900 before:content-[''] [&_.content-container]:overflow-hidden [&_.content-container]:overscroll-none [&_.content-container]:px-0 md:[&_.content-container]:overflow-visible",
        hasActiveCompactMenu
          ? 'md:!w-[495px] md:!max-w-[495px]'
          : 'md:!w-[245px] md:!max-w-[245px]',
        className,
      )}
      style={{ top: `${sidesheetTop}px`, height: `calc(100dvh - ${sidesheetTop}px)` }}
      overlayClassName={tw('z-50', '!backdrop-blur-0')}
      overlayStyle={{ top: `${sidesheetTop}px` }}
    >
      <div className="h-full w-full overflow-hidden md:overflow-visible">
        <div
          className={tw(
            'flex h-full w-max transition-transform duration-200 ease-out motion-reduce:transition-none',
            hasActiveCompactMenu
              ? '-translate-x-[245px] md:translate-x-0'
              : hasActiveContextualNavigation
                ? '-translate-x-[245px]'
                : 'translate-x-0',
          )}
        >
          <div className="pointer-events-none relative z-10 flex w-[245px] shrink-0 flex-col bg-gray-900 py-[14px]">
            <div
              aria-hidden={hasActiveContextualNavigation ? true : undefined}
              inert={hasActiveContextualNavigation ? true : undefined}
              className={tw(
                'relative flex min-h-0 flex-1 flex-col justify-between overflow-y-auto overscroll-none bg-gray-900 px-4 scroller-xs transition-[visibility] duration-200 ease-out motion-reduce:transition-none',
                hasActiveCompactMenu
                  ? 'pointer-events-none invisible md:pointer-events-auto md:visible'
                  : hasActiveContextualNavigation
                    ? 'pointer-events-none invisible'
                    : 'visible pointer-events-auto',
              )}
            >
              <StackedNavigationItems isParentOpen={open} onNavigate={handleNavigate} />
              {showUnlimitedPromo ? (
                <div className="flex flex-col items-start justify-end pt-4">
                  <GoUnlimitedMessage className="w-full" show={true} />
                </div>
              ) : null}
              {showCoinsPromo ? (
                <div className="flex flex-col items-start justify-end pt-4">
                  {isStudioAiUser ? (
                    <CoinMessages show={true} />
                  ) : (
                    <FreeAccessMessage className="w-full" show={true} />
                  )}
                  <div className="hidden w-full pt-4 md:flex">
                    <EarnMoreCoinsButton variant="tertiary" className="w-full" />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="pointer-events-auto relative flex w-full shrink-0 items-center justify-between gap-2 border-b border-theme_semantic_surface_seperator_color-faint bg-gray-900 px-4 pt-4 pb-3 md:hidden">
              <NavigationUsersOnline />
              {promo === 'coins' ? (
                <div className="flex shrink-0 items-center gap-2">
                  <EarnIconButton variant="secondary" />
                  <EarnMoreCoinsIconButton variant="tertiary" />
                </div>
              ) : null}
            </div>

            <div className="pointer-events-auto relative flex w-full shrink-0 flex-row items-center justify-end gap-1 bg-gray-900 px-4 pt-3 pb-[14px] md:pt-4">
              <div className="hidden md:contents">
                {compactNavigationMenus.map(menu => (
                  <ButtonTooltip key={menu.id} value={menu.label} side="top" delay={0} offset={6}>
                    <Button
                      iconOnly
                      aria-controls={panelId}
                      aria-expanded={activeMenuId === menu.id}
                      aria-label={menu.label}
                      onClick={() => {
                        onActiveMenuIdChange(activeMenuId === menu.id ? null : menu.id)
                      }}
                      size="sm"
                      variant={activeMenuId === menu.id ? 'secondary' : 'ghost'}
                    >
                      {menu.icon}
                    </Button>
                  </ButtonTooltip>
                ))}
              </div>
              <StackedNavigationFooter items={footerItems} onNavigate={handleNavigate} />
            </div>
          </div>

          {contextualNavigation || compactNavigationMenus.length > 0 ? (
            <div
              id={panelId}
              aria-hidden={!hasActiveMenu ? true : undefined}
              inert={!hasActiveMenu ? true : undefined}
              className={tw(
                'relative z-0 h-full min-w-0 w-[245px] shrink-0 overflow-y-auto overscroll-none border-l border-theme_semantic_surface_border_color-subtle bg-gray-900 px-4 scroller-xs transition-[transform,visibility] duration-200 ease-out motion-reduce:transition-none',
                hasActiveCompactMenu && 'md:h-[calc(100%+1rem)] md:w-[249px]',
                hasActiveMenu
                  ? 'pointer-events-auto visible md:translate-x-0'
                  : 'pointer-events-none invisible md:-translate-x-full',
              )}
            >
              {activeCompactMenu && ActiveCompactMenuContent ? (
                <>
                  <header className="mb-3 flex shrink-0 items-center gap-2 py-3 md:hidden">
                    <Button
                      iconOnly
                      aria-controls={panelId}
                      aria-expanded={hasActiveMenu}
                      aria-label={t('studio.common.back')}
                      onClick={() => onActiveMenuIdChange(null)}
                      size="xs"
                      variant="secondary"
                    >
                      <ChevronLeftIcon aria-hidden />
                    </Button>
                    <span className="min-w-0 flex-1 truncate studio-typography-14 text-theme_semantic_surface_ui_foreground-color-strong">
                      {activeCompactMenu.label}
                    </span>
                    <Button
                      iconOnly
                      aria-label="Close"
                      onClick={() => handleOpenChange(false)}
                      size="xs"
                      variant="secondary"
                    >
                      <XCloseIcon aria-hidden />
                    </Button>
                  </header>
                  <React.Suspense fallback={null}>
                    <ActiveCompactMenuContent onNavigate={handleNavigate} />
                  </React.Suspense>
                </>
              ) : null}
              {activeContextualNavigation ? (
                <ContextualNavigationPanel
                  expanded
                  navigation={activeContextualNavigation}
                  onBack={() => onActiveMenuIdChange(null)}
                  onNavigate={handleNavigate}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </LeftSheet>
  )
}

function CoinMessages({ show }: { show: boolean }) {
  const isStuioAiUserQuery = useIsStudioAIUserQuery()
  const isTrialUserQuery = useIsTrialUserQuery()

  const anyPending = isStuioAiUserQuery.isPending || isTrialUserQuery.isPending
  if (anyPending) {
    return null
  }
  if (isStuioAiUserQuery.data && isTrialUserQuery.data) {
    return <SkipTrialMessage className="w-full" show={show} />
  }
  return <CoinTopUpMessage className="w-full" show={show} />
}
